import LOGO from "@/assets/images/Prud_Logo.png";
import { useForm, FormProvider } from "react-hook-form";
import { useEffect } from "react";
import useAuth from "../hooks/useAuth";
import { handleSuccessToast } from "@/Components/Uitoast/globalToastHandler";
import { ArrowLeft } from "lucide-react";
import UiButton from "@/UI-Components/Buttons/UiButton";
import { setApiServiceHeader } from "@/lib/Axios/axios.config";
import { inputUtility } from "@/utlities/input.utility";
import UiTextInput from "@/UI-Components/Input/UiTextInput";

interface IPreAuthLoginFormSchema {
  mobile: string;
  otp: string;
}

const PreAuthLoginForm = ({ onToggle }: { onToggle: () => void }) => {
  const formMethods = useForm<IPreAuthLoginFormSchema>();
  const { handleSubmit, getValues, watch, setFocus } = formMethods;

  const {
    mutations: { loginUserMutation },
    states: {
      showOtpField,
      setShowOtpField,
      timer,
      setTimer,
      canResend,
      setCanResend,
    },
    routing: { navigate },
  } = useAuth();

  const mobileValues = watch("mobile");

  useEffect(() => {
    let interval: ReturnType<typeof setTimeout>;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0 && showOtpField) {
      setCanResend(true);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer, showOtpField]);

  const onSendOtp = (data: any) => {
    const payload = {
      mobile: data?.mobile,
      loginOption: "otp_code",
    };
    loginUserMutation.mutateAsync(payload).then(() => {
      handleSuccessToast({ message: "OTP Send Successfully" });
      setShowOtpField(true);
      setTimer(30);
      setCanResend(false);
    });
  };

  const onVerifyOtp = (data: any) => {
    const payload = {
      mobile: data?.mobile,
      loginOption: "otp_code",
      otp_code: data?.otp,
    };
    loginUserMutation.mutateAsync(payload).then((response) => {
      handleSuccessToast({ message: "Login Successfully" });
      if (response?.data) {
        localStorage.setItem("token", response?.data?.token);
        setApiServiceHeader({
          key: "Authorization",
          value: response?.data?.token,
        });
        if (response?.data?.vertical_type.id === 4) {
          navigate("/agent/profile-details");
        } else {
          navigate("/agent-master");
        }
      }
    });
  };

  const handleResendOtp = () => {
    const mobile = getValues("mobile");
    const payload = {
      mobile: Number(mobile),
    };
    if (mobile) {
      loginUserMutation.mutateAsync(payload).then(() => {
        setTimer(30);
        setCanResend(false);
        handleSuccessToast({ message: "OTP Send Successfully" });
      });
    }
  };

  const handleBackToMobile = () => {
    setShowOtpField(false);
    setTimer(0);
    setCanResend(false);
    formMethods.setValue("otp", "");
  };

  const onSubmit = (data: any) => {
    if (!showOtpField) {
      onSendOtp(data);
    } else {
      onVerifyOtp(data);
    }
  };

  useEffect(() => {
    if (showOtpField) {
      setFocus("otp");
    }
  }, [showOtpField, setFocus]);

  return (
    <div className="w-1/2 flex flex-col p-4 justify-center">
      <div className="flex items-center justify-center h-full">
        <div className="min-w-md">
          <div className="flex items-center justify-start w-full mb-2">
            <img src={LOGO} alt="Logo" className="h-12" />
          </div>

          <FormProvider {...formMethods}>
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              {!showOtpField ? (
                <>
                  <div className="flex flex-col">
                    <h1 className="text-lg font-bold ">Welcome Back</h1>
                    <p className="text-gray-500 text-sm ">
                      Please enter your mobile number
                    </p>
                  </div>

                  <div className="flex flex-col w-full gap-3">
                    <UiTextInput
                      name={"mobile"}
                      label={"Mobile Number"}
                      placeholder={"Enter your mobile number"}
                      maxLength={10}
                      registerOptions={{
                        required: "Mobile number is required",
                        pattern: {
                          value: inputUtility.indianMobileNumberRegex,
                          message: "Invalid Mobile Number",
                        },
                        onChange: (e) => {
                          inputUtility.onlyNumbers(e, 10);
                        },
                      }}
                      containerClass="gap-1"
                    />
                    <UiButton
                      type="submit"
                      buttonType="primary"
                      text="Send OTP"
                      disabled={mobileValues?.length !== 10}
                      isLoading={loginUserMutation.isPending}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-col justify-start items-start ">
                    <UiButton
                      type="button"
                      buttonType="tertiary"
                      icon={<ArrowLeft className="size-4" />}
                      text="Go Back"
                      onClick={handleBackToMobile}
                      className="text-sm text-primary flex items-center gap-1 flex-row-reverse  px-2 py-1"
                    />
                    <h1 className="text-lg font-bold">Verify OTP</h1>
                    <p className="text-gray-500 mb-4 text-sm ">
                      We&apos;ve sent a verification code to{" "}
                      {getValues("mobile")}
                    </p>
                  </div>

                  <div className="flex flex-col w-full gap-3">
                    <UiTextInput
                      key="otp-field"
                      name={"otp"}
                      label={"Enter OTP"}
                      placeholder={"000000"}
                      type="password"
                      className="text-center w-full h-12 text-lg tracking-[0.3em] font-semibold border-2 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      registerOptions={{
                        required: "OTP is required",
                        maxLength: {
                          value: 6,
                          message: "OTP must be 6 digits",
                        },
                        onChange: (e) => {
                          inputUtility.onlyNumbers(e, 6);
                        },
                      }}
                      containerClass="gap-1"
                    />

                    <div className="text-center text-sm flex justify-end ">
                      {timer > 0 ? (
                        <span className="text-gray-500">
                          Resend OTP in {timer}s
                        </span>
                      ) : (
                        canResend && (
                          <UiButton
                            type="button"
                            buttonType="tertiary"
                            text="Resend OTP"
                            onClick={handleResendOtp}
                            className="text-xs text-primary"
                          />
                        )
                      )}
                    </div>
                    <div className="flex w-full">
                      <UiButton
                        type="submit"
                        buttonType="primary"
                        text="Verify OTP"
                        className="flex-1"
                        isLoading={loginUserMutation.isPending}
                      />
                    </div>
                  </div>
                </>
              )}
            </form>
          </FormProvider>

          <div className="mt-4 text-center text-sm text-gray-500 flex justify-center items-center gap-2">
            <span>Don&apos;t have an account? </span>
            <UiButton
              buttonType="tertiary"
              text="Sign Up"
              onClick={onToggle}
              className="text-primary"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreAuthLoginForm;
